import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import ArticleCard from './ArticleCard';

const SectionContainer = styled.div`
  padding: 2rem 0;
`;

const SectionHeader = styled.div`
  margin-bottom: 2rem;
  border-bottom: 2px solid ${props => props.theme.colors.primary};
  padding-bottom: 1rem;
`;

const SectionTitle = styled.h1`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  text-transform: uppercase;
`;

const ArticleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const LoadMoreButton = styled.button`
  background-color: white;
  border: 2px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 600;
  padding: 0.75rem 2rem;
  border-radius: 4px;
  margin: 3rem auto 0;
  display: block;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const NoArticlesMessage = styled.div`
  text-align: center;
  padding: 3rem 0;
  font-family: ${props => props.theme.fonts.accent};
  color: ${props => props.theme.colors.text};
  opacity: 0.6;
`;

const SectionPage = ({ section: propSection }) => {
  const { section: paramSection } = useParams();
  const section = propSection || paramSection;
  
  const [articles, setArticles] = useState([]);
  const [visibleCount, setVisibleCount] = useState(9);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        
        // In a real implementation, you would fetch articles from Firestore
        // For demo purposes, create placeholder articles
        const placeholderArticles = [];
        
        // Generate 20 placeholder articles
        for (let i = 0; i < 20; i++) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          
          placeholderArticles.push({
            id: `${section}-${i}`,
            title: getSectionArticleTitle(section, i),
            image_url: `https://source.unsplash.com/random/800x600/?${section},${i}`,
            author: getRandomAuthor(),
            published_at: date.toISOString(),
            section: section
          });
        }
        
        setArticles(placeholderArticles);
        setLoading(false);
      } catch (error) {
        console.error(`Error fetching ${section} articles:`, error);
        setLoading(false);
      }
    };
    
    fetchArticles();
  }, [section]);
  
  const loadMore = () => {
    setVisibleCount(prev => prev + 6);
  };
  
  // Helper function to generate random article titles based on section
  const getSectionArticleTitle = (section, index) => {
    const titles = {
      news: [
        'School Board Approves New STEM Building',
        'Annual Food Drive Exceeds Donation Goals',
        'New Schedule Changes Coming Next Semester',
        'Principal Announces Retirement After 15 Years',
        'Student Council Launches Mental Health Initiative'
      ],
      sports: [
        'Spartans Win Championship in Dramatic Overtime',
        'Track Team Breaks Three School Records',
        'Basketball Star Commits to Division I College',
        'Swim Team Qualifies Six for State Championships',
        'New Athletic Director Announces Facility Upgrades'
      ],
      features: [
        'Student Spotlight: The Young Entrepreneur',
        'Behind the Scenes of the Spring Musical',
        'Teacher of the Year Profile: Ms. Rodriguez',
        'Exchange Students Share Their Experience at Schurr',
        'Club Spotlight: Robotics Team Heads to Nationals'
      ],
      opinion: [
        'Editorial: Why School Spirit Matters',
        'Letter from the Editor: Community Service',
        'Opinion: We Need More Diverse Literature Classes',
        'Commentary: The Case for Later School Start Times',
        'Student Voice: The Importance of Arts Education'
      ]
    };
    
    const sectionTitles = titles[section] || titles.news;
    return sectionTitles[index % sectionTitles.length];
  };
  
  // Helper function to generate random author names
  const getRandomAuthor = () => {
    const authors = [
      'Jane Doe',
      'John Smith',
      'Maria Garcia',
      'David Lee',
      'Sophia Martinez',
      'James Wilson'
    ];
    
    return authors[Math.floor(Math.random() * authors.length)];
  };
  
  // Format section title for display
  const formatSectionTitle = (section) => {
    return section.charAt(0).toUpperCase() + section.slice(1);
  };
  
  return (
    <SectionContainer>
      <div className="container">
        <SectionHeader>
          <SectionTitle>{formatSectionTitle(section)}</SectionTitle>
        </SectionHeader>
        
        {articles.length > 0 ? (
          <>
            <ArticleGrid>
              {articles.slice(0, visibleCount).map(article => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </ArticleGrid>
            
            {visibleCount < articles.length && (
              <LoadMoreButton onClick={loadMore}>
                Load More Articles
              </LoadMoreButton>
            )}
          </>
        ) : (
          <NoArticlesMessage>
            {loading ? 'Loading articles...' : `No ${section} articles found.`}
          </NoArticlesMessage>
        )}
      </div>
    </SectionContainer>
  );
};

export default SectionPage;